/**
 * LevNutri — Módulo de Relatórios
 * Prontuário Interno + Resumo para a Paciente
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ordenarRefeicoes } from '../PlanoAlimentar/helpers';
import ReactDOM from 'react-dom';
import { patientsService, reportsService } from '../../services/api';
import ReportEmail from './ReportEmail';
import DocumentoPacienteModal from './DocumentoPacienteModal';
import useIsMobile from '../../hooks/useIsMobile';
import { Ico, emo } from '../ui/Ico';
import { useMarca, LOGO_LEVNUTRI, reportLogoStyle } from '../../branding';

const BRAND    = '#D4A574';
const BRAND2   = '#b8895c';
const FONT     = 'Nunito, sans-serif';
const SERIF    = 'Nunito, sans-serif';

// ─── Mapas de labels ──────────────────────────────────────────────────────────

const OBJECTIVES = {
  perda_peso:   { label: 'Perda de Peso Saudável',   icon: '⚖️'  },
  ganho_peso:   { label: 'Ganho de Peso',             icon: '📈'  },
  manutencao:   { label: 'Manutenção do Peso',        icon: '✅'  },
  ganho_massa:  { label: 'Ganho de Massa Muscular',   icon: '💪'  },
};

const SERVICES = {
  consulta_personalizada:  { label: 'Consulta Nutricional Personalizada', icon: '📋' },
  emagrecimento_feminino:  { label: 'Programa de Emagrecimento Feminino', icon: '🔥' },
  equilibrio_hormonal:     { label: 'Nutrição p/ Equilíbrio Hormonal',    icon: '⚡' },
  mulheres_ativas:         { label: 'Nutrição para Mulheres Ativas',      icon: '💪' },
  reeducacao_compulsao:    { label: 'Reeducação Alimentar',               icon: '🌱' },
  planejamento_especializado: { label: 'Planejamento Alimentar Esp.',     icon: '🎯' },
  acompanhamento_continuo: { label: 'Acompanhamento Contínuo',            icon: '🔄' },
};

const MEAL_COLORS = {
  cafe_manha:   '#e8c9a8', lanche_manha: '#b8c7d8', almoco:       '#b9d2bd',
  lanche_tarde: '#e3c48a', jantar:       '#b8c7d8', ceia:         '#b8c7d8',
  pre_treino:   '#e8c9a8', pos_treino:   '#b9d2bd',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function calcAge(birthDate) {
  if (!birthDate) return null;
  const hoje = new Date();
  const nasc = new Date(birthDate + 'T12:00:00');
  let age = hoje.getFullYear() - nasc.getFullYear();
  const m = hoje.getMonth() - nasc.getMonth();
  if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) age--;
  return age > 0 ? age : null;
}

function fmtDate(d) {
  if (!d) return '-';
  return new Date(d + 'T12:00:00').toLocaleDateString('pt-BR');
}

function fmtNum(v, decimals = 1) {
  const n = parseFloat(v);
  return isNaN(n) ? '-' : n.toFixed(decimals);
}

function classifyIMC(imc) {
  const v = parseFloat(imc);
  if (isNaN(v)) return null;
  if (v < 18.5) return { label: 'Abaixo do peso',    color: '#7189a6', icon: '⚠️' };
  if (v < 25.0) return { label: 'Peso normal',        color: '#46714f', icon: '✅' };
  if (v < 30.0) return { label: 'Sobrepeso',          color: '#c4933f', icon: '⚠️' };
  return              { label: 'Obesidade',            color: '#a8483f', icon: '🔴' };
}

function classifyGordura(gc, gender) {
  const v = parseFloat(gc);
  if (isNaN(v)) return null;
  const isMale = gender === 'M';
  if (isMale) {
    if (v < 8)  return { label: 'Muito baixo', color: '#7189a6', icon: '⚠️' };
    if (v < 20) return { label: 'Normal',       color: '#46714f', icon: '✅' };
    if (v < 25) return { label: 'Moderado',     color: '#c4933f', icon: '⚠️' };
    return            { label: 'Alto',           color: '#a8483f', icon: '🔴' };
  } else {
    if (v < 13) return { label: 'Muito baixo', color: '#7189a6', icon: '⚠️' };
    if (v < 26) return { label: 'Normal',       color: '#46714f', icon: '✅' };
    if (v < 33) return { label: 'Moderado',     color: '#c4933f', icon: '⚠️' };
    return            { label: 'Alto',           color: '#a8483f', icon: '🔴' };
  }
}

function classifyVisceral(gv) {
  const v = parseFloat(gv);
  if (isNaN(v)) return null;
  if (v <= 9)  return { label: 'Normal',     color: '#46714f', icon: '✅' };
  if (v <= 14) return { label: 'Alto',       color: '#c4933f', icon: '⚠️' };
  return              { label: 'Muito alto', color: '#a8483f', icon: '🔴' };
}

function classifyMuscle(ms, gender) {
  const v = parseFloat(ms);
  if (isNaN(v)) return null;
  const isMale = gender === 'M';
  const low = isMale ? 33 : 24;
  const norm = isMale ? 36 : 28;
  if (v < low)  return { label: 'Baixo',      color: '#c4933f', icon: '⚠️' };
  if (v < norm) return { label: 'Normal',      color: '#46714f', icon: '✅' };
  return              { label: 'Elevado',      color: '#46714f', icon: '💪' };
}

function mealList(meals) {
  if (!meals) return [];
  return ordenarRefeicoes(meals).filter(([, m]) => m.alimentos?.length > 0).map(([k, m]) => ({ ...m, _key: k }));
}

// ─── CSS de impressão ─────────────────────────────────────────────────────────

export function RelatorioCss() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700;800&display=swap');

      @media screen {
        .lev-relatorio-print { display: none !important; }
      }
      @media print {
        body > *:not(.lev-relatorio-print) { display: none !important; }
        body > .lev-relatorio-print { display: block !important; }
        @page { size: A4 portrait; margin: 1.2cm 1.6cm; }
        .no-break { page-break-inside: avoid; break-inside: avoid; }
        .page-break { page-break-before: always; break-before: always; }
        img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `}</style>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PRONTUÁRIO INTERNO — CONTEÚDO
// ══════════════════════════════════════════════════════════════════════════════

function ProntuarioContent({ data, notes }) {
  const marca = useMarca();
  const LOGO_URL = marca.logo || LOGO_LEVNUTRI;
  const { patient, nutri, anamnese, bio_records, total_consultas,
          primeira_consulta, evolucao, diet_plan, diet_plans_history } = data;

  const age = calcAge(patient.birth_date);
  const obj = OBJECTIVES[patient.objective] || { label: patient.objective || '-', icon: '🎯' };

  const T = { // text styles
    label: { margin: 0, fontSize: '7pt', color: '#736b64', fontFamily: FONT, textTransform: 'uppercase', letterSpacing: '0.04em' },
    val:   { margin: '1px 0 0', fontSize: '8.5pt', color: '#2f2a26', fontFamily: FONT, fontWeight: 500 },
    h2:    { fontFamily: SERIF, fontSize: '9pt', fontWeight: 600, color: '#2f2a26', margin: '0 0 5pt',
             borderBottom: '0.8pt solid #d9d0c6', paddingBottom: '3pt' },
    section: { marginBottom: '10pt', pageBreakInside: 'avoid', breakInside: 'avoid' },
  };

  // Bio table columns
  const bioCols = [
    { key: 'data_medicao',         label: 'Data',      fmt: (v) => fmtDate(v) },
    { key: 'peso',                 label: 'Peso (kg)', fmt: (v) => fmtNum(v) },
    { key: 'gordura_corporal',     label: 'Gordura%',  fmt: (v) => fmtNum(v) },
    { key: 'musculos_esqueleticos',label: 'Músculo%',  fmt: (v) => fmtNum(v) },
    { key: 'imc',                  label: 'IMC',       fmt: (v) => fmtNum(v) },
    { key: 'gordura_visceral',     label: 'Visceral',  fmt: (v) => v || '-'  },
    { key: 'metabolismo_basal',    label: 'Metab.',    fmt: (v) => v ? `${v} kcal` : '-' },
    { key: 'idade_corporal',       label: 'Id. Corp.', fmt: (v) => v ? `${v}a` : '-' },
  ];

  const goodDown = (k) => ['peso','gordura_corporal','gordura_visceral','imc','idade_corporal'].includes(k);
  const varColor = (k, d) => {
    if (d === undefined) return '#736b64';
    return goodDown(k) ? (d < 0 ? '#46714f' : d > 0 ? '#a8483f' : '#736b64')
                       : (d > 0 ? '#46714f' : d < 0 ? '#a8483f' : '#736b64');
  };

  return (
    <div style={{ fontFamily: FONT, color: '#2f2a26', fontSize: '8pt', lineHeight: 1.5 }}>

      {/* CABEÇALHO */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        paddingBottom: '6pt', marginBottom: '4pt', borderBottom: `1.5pt solid ${BRAND}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8pt' }}>
          <img src={LOGO_URL} alt={marca.businessName || 'LevNutri'} style={{ ...reportLogoStyle(marca, 28), objectFit: 'contain' }}/>
          <div>
            <p style={{ margin: 0, fontSize: '8pt', fontWeight: 600, color: '#2f2a26', fontFamily: FONT }}>
              {nutri?.name_display || 'Nutricionista'}
            </p>
            {nutri?.crn && <p style={{ margin: 0, fontSize: '7pt', color: '#736b64', fontFamily: FONT }}>{nutri.crn}</p>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ display: 'inline-block', background: '#a8483f', color: '#ffffff', padding: '2pt 8pt',
            borderRadius: '3pt', fontSize: '6.5pt', fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', marginBottom: '3pt' }}>
            ● DOCUMENTO INTERNO — CONFIDENCIAL
          </div>
          <p style={{ margin: 0, fontSize: '7pt', color: '#736b64', fontFamily: FONT }}>
            Gerado em {new Date().toLocaleString('pt-BR')}
          </p>
        </div>
      </div>

      {/* Título */}
      <h1 style={{ fontFamily: SERIF, fontSize: '14pt', fontWeight: 600, margin: '4pt 0 10pt',
        color: '#2f2a26', textAlign: 'center' }}>
        Prontuário da Paciente — Uso Exclusivo da Nutricionista
      </h1>

      {/* IDENTIFICAÇÃO */}
      <div style={T.section} className="no-break">
        <p style={T.h2}>1. Identificação da Paciente</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4pt 10pt' }}>
          {[
            ['Nome', patient.name],
            ['Idade', age ? `${age} anos` : '-'],
            ['Data de Nascimento', fmtDate(patient.birth_date)],
            ['Gênero', patient.gender === 'M' ? 'Masculino' : patient.gender === 'F' ? 'Feminino' : 'Outro'],
            ['E-mail', patient.email || '-'],
            ['Telefone', patient.phone || '-'],
            ['Primeira Consulta', fmtDate(primeira_consulta)],
            ['Total de Consultas', `${total_consultas}`],
            ['Objetivo', obj.label],
            ['Peso Meta', patient.target_weight ? `${patient.target_weight} kg` : '-'],
            ['Altura Inicial', patient.initial_height ? `${parseFloat(patient.initial_height)} cm` : '-'],
            ['Status', patient.status || 'ativo'],
          ].map(([l, v]) => (
            <div key={l}>
              <p style={T.label}>{l}</p>
              <p style={T.val}>{v}</p>
            </div>
          ))}
        </div>
        {patient.notes && (
          <div style={{ marginTop: '5pt', padding: '4pt 6pt', background: '#fbf8f5', border: '0.5pt solid #e8c9a8', borderRadius: '3pt' }}>
            <p style={{ ...T.label, marginBottom: '1pt' }}>Observações</p>
            <p style={{ ...T.val, fontStyle: 'italic' }}>{patient.notes}</p>
          </div>
        )}
      </div>

      {/* ANAMNESE */}
      {anamnese && (
        <div style={T.section} className="no-break">
          <p style={T.h2}>2. Resumo da Anamnese</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4pt 14pt' }}>
            {[
              ['Queixa Principal', anamnese.queixa_principal],
              ['Histórico de Doenças', anamnese.historico_doencas],
              ['Medicamentos em Uso', anamnese.medicamentos_uso],
              ['Alergias / Intolerâncias', anamnese.intolerâncias_alergias],
              ['Preferências Alimentares', anamnese.preferencias_alimentares],
              ['Restrições', anamnese.horarios_refeicoes],
              ['Atividade Física', anamnese.pratica_atividade_fisica
                ? `${anamnese.tipo_atividade || ''} — ${anamnese.frequencia_atividade || ''} — ${anamnese.duracao_atividade ? anamnese.duracao_atividade + ' min' : ''}`
                : 'Não pratica'],
              ['Qualidade do Sono', `${anamnese.horas_sono || '-'}h/noite — ${anamnese.qualidade_sono || '-'}`],
              ['Consumo de Água', anamnese.consumo_agua ? `${anamnese.consumo_agua} L/dia` : '-'],
              ['Hábito Intestinal', `${anamnese.habito_intestinal || '-'} — ${anamnese.frequencia_intestinal || '-'}`],
              ['Álcool', anamnese.consumo_alcool || '-'],
              ['Histórico Familiar', anamnese.historico_familiar],
            ].filter(([, v]) => v).map(([l, v]) => (
              <div key={l}>
                <p style={T.label}>{l}</p>
                <p style={T.val}>{v}</p>
              </div>
            ))}
          </div>
          {anamnese.observacoes && (
            <div style={{ marginTop: '4pt', padding: '4pt 6pt', background: '#fbf8f5', border: '0.5pt solid #e8c9a8', borderRadius: '3pt' }}>
              <p style={{ ...T.label, marginBottom: '1pt' }}>Observações da Anamnese</p>
              <p style={{ ...T.val, fontStyle: 'italic' }}>{anamnese.observacoes}</p>
            </div>
          )}
        </div>
      )}

      {/* BIOIMPEDÂNCIA */}
      {bio_records && bio_records.length > 0 && (
        <div style={T.section}>
          <p style={T.h2}>3. Evolução da Bioimpedância — {bio_records.length} medição{bio_records.length !== 1 ? 'ões' : ''}</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7.5pt', fontFamily: FONT }}>
            <thead>
              <tr style={{ background: '#fbf8f5' }}>
                {bioCols.map(c => (
                  <th key={c.key} style={{ padding: '3pt 4pt', textAlign: 'center', fontWeight: 600, color: '#4a4440',
                    borderBottom: '0.8pt solid #d9d0c6' }}>{emo(c.label)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bio_records.map((r, i) => {
                const isLast = i === bio_records.length - 1;
                return (
                  <tr key={r.id} style={{
                    background: isLast ? '#fbf8f5' : i % 2 === 0 ? '#ffffff' : '#fbf8f5',
                    fontWeight: isLast ? 700 : 400,
                    borderBottom: '0.3pt solid #ebe3da',
                  }}>
                    {bioCols.map(c => (
                      <td key={c.key} style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>
                        {isLast && i > 0 ? <strong>{c.fmt(r[c.key])}</strong> : c.fmt(r[c.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {/* Variação total */}
              {evolucao?.tem_evolucao && (
                <tr style={{ background: '#fbf8f5', borderTop: '1pt solid #d9d0c6' }}>
                  <td style={{ padding: '2.5pt 4pt', fontWeight: 600, fontSize: '7pt', color: '#4a4440' }}>
                    Variação Total
                  </td>
                  {bioCols.slice(1).map(c => {
                    const d = evolucao.variacoes?.[c.key];
                    return (
                      <td key={c.key} style={{ padding: '2.5pt 4pt', textAlign: 'center', fontWeight: 600,
                        color: d !== undefined ? varColor(c.key, d) : '#736b64' }}>
                        {d !== undefined ? `${d > 0 ? '+' : ''}${d}` : '-'}
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ÚLTIMO PLANO ALIMENTAR */}
      {diet_plan && (
        <div style={T.section} className="no-break">
          <p style={T.h2}>4. Último Plano Alimentar Ativo</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '4pt 10pt', marginBottom: '6pt' }}>
            <div><p style={T.label}>Nome do Plano</p><p style={T.val}>{diet_plan.plan_name}</p></div>
            <div><p style={T.label}>Data</p><p style={T.val}>{fmtDate(diet_plan.start_date)}</p></div>
            <div><p style={T.label}>Serviço</p><p style={T.val}>{emo((SERVICES[diet_plan.service_type]?.label) || diet_plan.service_type || '-')}</p></div>
            <div><p style={T.label}>Calorias/dia</p><p style={{ ...T.val, fontWeight: 600, color: BRAND2 }}>{diet_plan.daily_calories} kcal</p></div>
            <div><p style={T.label}>Proteínas</p><p style={T.val}>{fmtNum(diet_plan.protein_grams)} g</p></div>
            <div><p style={T.label}>Carboidratos</p><p style={T.val}>{fmtNum(diet_plan.carbs_grams)} g</p></div>
            <div><p style={T.label}>Gorduras</p><p style={T.val}>{fmtNum(diet_plan.fat_grams)} g</p></div>
            <div><p style={T.label}>Água</p><p style={T.val}>{diet_plan.water_recommendation} L/dia</p></div>
            <div><p style={T.label}>Gerado por</p><p style={T.val}>{emo(diet_plan.ai_generated ? '✨ IA (GPT-4)' : 'Manualmente')}</p></div>
          </div>
          {/* Refeições compactas */}
          {mealList(diet_plan.meals_plan).length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3pt 8pt' }}>
              {mealList(diet_plan.meals_plan).map((meal, i) => (
                <div key={i} style={{ padding: '4pt 6pt', background: '#fbf8f5',
                  border: '0.5pt solid #f5ece2', borderRadius: '3pt' }}>
                  <p style={{ margin: '0 0 2pt', fontWeight: 600, fontSize: '7.5pt', color: '#4a4440', fontFamily: FONT }}>
                    {emo(meal.nome)} {meal.horario ? `(${meal.horario})` : ''} — {meal.calorias || 0} kcal
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '10pt', fontSize: '7pt', color: '#736b64' }}>
                    {(meal.alimentos || []).map((a, j) => (
                      <li key={j}>{a.alimento} — {a.porcao}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
          {/* Suplementos */}
          {diet_plan.supplements?.length > 0 && (
            <div style={{ marginTop: '5pt' }}>
              <p style={{ ...T.label, marginBottom: '2pt' }}>Suplementação Prescrita</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4pt' }}>
                {diet_plan.supplements.map((s, i) => (
                  <span key={i} style={{ padding: '2pt 6pt', background: '#edf1f6',
                    border: '0.5pt solid #b8c7d8', borderRadius: '10pt',
                    fontSize: '7.5pt', color: '#4f6a86', fontFamily: FONT }}>
                    {emo(s.nome)} {s.dosagem ? `(${s.dosagem})` : ''} {s.momento_uso ? `— ${s.momento_uso}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* HISTÓRICO DE PLANOS */}
      {diet_plans_history?.length > 0 && (
        <div style={T.section} className="no-break">
          <p style={T.h2}>5. Histórico de Planos Alimentares</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '7.5pt', fontFamily: FONT }}>
            <thead>
              <tr style={{ background: '#fbf8f5' }}>
                {['Data', 'Nome do Plano', 'Serviço', 'Kcal/dia', 'Prot.', 'Carb.', 'Gord.', 'Tipo', 'Status'].map(h => (
                  <th key={h} style={{ padding: '3pt 4pt', textAlign: h === 'Nome do Plano' ? 'left' : 'center',
                    fontWeight: 600, color: '#4a4440', borderBottom: '0.8pt solid #d9d0c6' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {diet_plans_history.map((p, i) => (
                <tr key={p.id} style={{ borderBottom: '0.3pt solid #ebe3da',
                  background: i % 2 === 0 ? '#ffffff' : '#fbf8f5' }}>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>{fmtDate(p.start_date)}</td>
                  <td style={{ padding: '2.5pt 4pt' }}>{p.plan_name}</td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>
                    {emo(SERVICES[p.service_type]?.icon || '')} {SERVICES[p.service_type]?.label?.split(' ').slice(0,2).join(' ') || p.service_type || '-'}
                  </td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center', fontWeight: 600 }}>{p.daily_calories}</td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>{fmtNum(p.protein_grams)}g</td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>{fmtNum(p.carbs_grams)}g</td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>{fmtNum(p.fat_grams)}g</td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center' }}>
                    {emo(p.ai_generated ? '✨ IA' : 'Manual')}
                  </td>
                  <td style={{ padding: '2.5pt 4pt', textAlign: 'center', color: p.status === 'ativo' ? '#46714f' : '#736b64' }}>
                    {emo(p.status || 'ativo')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ALERTAS E LEMBRETES */}
      <div style={{ ...T.section, marginTop: '6pt' }} className="no-break">
        <p style={T.h2}>6. Alertas e Lembretes para Esta Consulta</p>
        <div style={{ minHeight: '60pt', padding: '8pt', background: '#fbf8f5',
          border: '1pt solid #e8c9a8', borderRadius: '5pt' }}>
          {notes ? (
            <p style={{ margin: 0, fontSize: '8.5pt', color: '#5a3e2b', fontFamily: FONT, lineHeight: 1.7,
              whiteSpace: 'pre-wrap' }}>{notes}</p>
          ) : (
            <p style={{ margin: 0, fontSize: '8pt', color: '#d9d0c6', fontStyle: 'italic', fontFamily: FONT }}>
              Sem anotações para esta consulta.
            </p>
          )}
        </div>
      </div>

      {/* RODAPÉ */}
      <div style={{ borderTop: '0.8pt solid #d9d0c6', paddingTop: '5pt', marginTop: '8pt',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ margin: 0, fontSize: '6.5pt', color: '#d9d0c6', fontFamily: FONT }}>
          {marca.businessName ? `${marca.businessName} • ` : ''}Gerado com LevNutri
        </p>
        <p style={{ margin: 0, fontSize: '6.5pt', color: '#a8483f', fontWeight: 600, fontFamily: FONT }}>
          CONFIDENCIAL — USO INTERNO DA NUTRICIONISTA
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// RESUMO DO PACIENTE — CONTEÚDO
// ══════════════════════════════════════════════════════════════════════════════

function ResumoPacienteContent({ data }) {
  const marca = useMarca();
  const LOGO_URL = marca.logo || LOGO_LEVNUTRI;
  const { patient, nutri, evolucao, diet_plan, proximo_retorno } = data;

  const age    = calcAge(patient.birth_date);
  const gender = patient.gender || 'F';
  const obj    = OBJECTIVES[patient.objective] || { label: patient.objective || 'Saúde e bem-estar', icon: '🎯' };
  const last   = evolucao?.ultima;

  const BioCard = ({ label, value, unit, cls }) => (
    <div style={{ padding: '8pt 10pt', background: '#ffffff', border: `1.5pt solid ${cls?.color || '#d9d0c6'}`,
      borderRadius: '8pt', textAlign: 'center', boxShadow: '0 1pt 3pt rgba(0,0,0,.06)' }}>
      <p style={{ margin: '0 0 2pt', fontSize: '6.5pt', color: '#736b64', textTransform: 'uppercase',
        letterSpacing: '0.06em', fontFamily: FONT }}>{emo(label)}</p>
      <p style={{ margin: 0, fontSize: '13pt', fontWeight: 700, color: '#2f2a26', fontFamily: FONT }}>
        {value}{unit}
      </p>
      {cls && (
        <p style={{ margin: '2pt 0 0', fontSize: '7pt', fontWeight: 600, color: cls.color, fontFamily: FONT }}>
          {emo(cls.icon)} {emo(cls.label)}
        </p>
      )}
    </div>
  );

  const goodDown = (k) => ['peso','gordura_corporal','gordura_visceral','imc','idade_corporal'].includes(k);

  const bioCards = last ? [
    { label: 'Peso',             value: fmtNum(last.peso),                 unit: ' kg',   cls: null },
    { label: '% Gordura Corp.',  value: fmtNum(last.gordura_corporal),      unit: '%',     cls: classifyGordura(last.gordura_corporal, gender) },
    { label: '% Músculos Esq.', value: fmtNum(last.musculos_esqueleticos), unit: '%',     cls: classifyMuscle(last.musculos_esqueleticos, gender) },
    { label: 'IMC',              value: fmtNum(last.imc),                  unit: '',      cls: classifyIMC(last.imc) },
    { label: 'Gordura Visceral', value: last.gordura_visceral || '-',       unit: ' nív.', cls: classifyVisceral(last.gordura_visceral) },
    { label: 'Metabolismo Basal',value: last.metabolismo_basal || '-',      unit: ' kcal', cls: null },
  ] : [];

  const ageCorp = last?.idade_corporal;
  const ageDiff = ageCorp && age ? ageCorp - age : null;

  return (
    <div style={{ fontFamily: FONT, color: '#2f2a26', background: '#ffffff' }}>

      {/* CABEÇALHO ELEGANTE */}
      <div style={{ textAlign: 'center', paddingBottom: '12pt', borderBottom: `2pt solid ${BRAND}`,
        marginBottom: '14pt' }}>
        <img src={LOGO_URL} alt={marca.businessName || 'LevNutri'} style={{ ...reportLogoStyle(marca, 40), objectFit: 'contain', marginBottom: '6pt' }}/>
        <h1 style={{ fontFamily: SERIF, fontSize: '20pt', fontWeight: 600, color: '#2f2a26',
          margin: '0 0 4pt' }}>
          Seu Resumo de Consulta
        </h1>
        <p style={{ margin: '0 0 2pt', fontSize: '9pt', color: '#4a4440', fontFamily: FONT }}>
          {nutri?.name_display || 'Nutricionista'}{nutri?.crn ? ` — ${nutri.crn}` : ''}
        </p>
        <p style={{ margin: 0, fontSize: '8pt', color: '#736b64', fontFamily: FONT }}>
          {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* SAUDAÇÃO */}
      <div className="no-break" style={{ marginBottom: '14pt', textAlign: 'center' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: '15pt', fontWeight: 600, color: BRAND2,
          margin: '0 0 6pt' }}>
          Olá, {emo(patient.name.split(' ')[0])}! <Ico e="🌸" />
        </h2>
        <p style={{ fontSize: '9pt', color: '#4a4440', maxWidth: '420pt', margin: '0 auto',
          lineHeight: 1.7, fontFamily: FONT }}>
          Aqui está um resumo da sua consulta de hoje com todas as informações importantes
          para acompanhar a sua jornada de saúde. Guarde este documento com carinho! <Ico e="💛" />
        </p>
      </div>

      {/* SEU PERFIL */}
      <div className="no-break" style={{ display: 'flex', gap: '10pt', marginBottom: '14pt',
        padding: '10pt 14pt', background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
        borderRadius: '8pt', border: `1pt solid ${BRAND}30` }}>
        <div style={{ flex: 1 }}>
          <p style={{ margin: '0 0 1pt', fontSize: '7pt', color: '#736b64', textTransform: 'uppercase',
            letterSpacing: '0.06em' }}>Nome</p>
          <p style={{ margin: 0, fontSize: '11pt', fontWeight: 600, color: '#2f2a26', fontFamily: SERIF }}>
            {patient.name}
          </p>
        </div>
        {age && (
          <div style={{ flex: '0 0 auto' }}>
            <p style={{ margin: '0 0 1pt', fontSize: '7pt', color: '#736b64', textTransform: 'uppercase',
              letterSpacing: '0.06em' }}>Idade</p>
            <p style={{ margin: 0, fontSize: '11pt', fontWeight: 600, color: '#2f2a26' }}>{age} anos</p>
          </div>
        )}
        <div style={{ flex: 1, textAlign: 'right' }}>
          <p style={{ margin: '0 0 1pt', fontSize: '7pt', color: '#736b64', textTransform: 'uppercase',
            letterSpacing: '0.06em' }}>Objetivo</p>
          <p style={{ margin: 0, fontSize: '10pt', fontWeight: 600, color: BRAND2 }}>
            {emo(obj.icon)} {emo(obj.label)}
          </p>
        </div>
      </div>

      {/* COMPOSIÇÃO CORPORAL */}
      {last && (
        <div className="no-break" style={{ marginBottom: '14pt' }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '11pt', fontWeight: 600, color: '#2f2a26',
            margin: '0 0 8pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
            Sua Composição Corporal Hoje — {fmtDate(last.data_medicao)}
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6pt' }}>
            {bioCards.map((c, i) => (
              <BioCard key={i} {...c}/>
            ))}
          </div>
          {/* Idade corporal destaque */}
          {ageDiff !== null && (
            <div style={{ marginTop: '8pt', padding: '8pt 12pt', textAlign: 'center',
              background: ageDiff <= 0 ? '#edf4ee' : '#fbf8f5',
              border: `1pt solid ${ageDiff <= 0 ? '#b9d2bd' : '#e8c9a8'}`,
              borderRadius: '8pt' }}>
              <p style={{ margin: 0, fontSize: '9.5pt', fontWeight: 600,
                color: ageDiff <= 0 ? '#46714f' : '#8a5f1a', fontFamily: FONT }}>
                {emo(ageDiff <= -3
                  ? `🎉 Sua idade corporal é ${Math.abs(ageDiff)} anos mais jovem que sua idade real!`
                  : ageDiff <= 2
                  ? `✅ Sua idade corporal está equivalente à sua idade real. Ótimo equilíbrio!`
                  : `⚠️ Sua idade corporal está ${ageDiff} anos acima da sua idade real. Vamos melhorar juntas!`)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* EVOLUÇÃO */}
      <div className="no-break" style={{ marginBottom: '14pt' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: '11pt', fontWeight: 600, color: '#2f2a26',
          margin: '0 0 8pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
          Sua Evolução
        </h2>
        {evolucao?.tem_evolucao ? (() => {
          const { primeira, ultima, variacoes } = evolucao;
          const pesoDiff = variacoes?.peso;
          return (
            <div>
              {/* Destaque perda/ganho de peso */}
              {pesoDiff !== undefined && (
                <div style={{ marginBottom: '8pt', padding: '8pt 14pt', textAlign: 'center',
                  background: pesoDiff < 0 ? 'linear-gradient(135deg,#edf4ee,#edf4ee)'
                                           : 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
                  border: `1.5pt solid ${pesoDiff < 0 ? '#46714f' : BRAND}`,
                  borderRadius: '8pt' }}>
                  <p style={{ margin: 0, fontSize: '11pt', fontWeight: 600,
                    color: pesoDiff < 0 ? '#2f5236' : '#8a5f1a', fontFamily: SERIF }}>
                    {emo(pesoDiff < 0
                      ? `💪 Você perdeu ${Math.abs(pesoDiff)} kg desde a primeira consulta!`
                      : pesoDiff > 0
                      ? `📈 Você ganhou ${pesoDiff} kg desde a primeira consulta.`
                      : `✅ Seu peso está estável desde a primeira consulta.`)}
                  </p>
                </div>
              )}
              {/* Tabela antes/depois */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8.5pt', fontFamily: FONT }}>
                <thead>
                  <tr style={{ background: '#fbf8f5' }}>
                    {['Indicador', `Início (${fmtDate(primeira.data_medicao)})`, `Hoje (${fmtDate(ultima.data_medicao)})`, 'Variação'].map(h => (
                      <th key={h} style={{ padding: '4pt 6pt', textAlign: h === 'Indicador' ? 'left' : 'center',
                        color: '#4a4440', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { k: 'peso',                  label: 'Peso',             unit: ' kg'   },
                    { k: 'gordura_corporal',       label: 'Gordura Corporal', unit: '%'     },
                    { k: 'musculos_esqueleticos',  label: 'Músculos Esq.',    unit: '%'     },
                    { k: 'imc',                    label: 'IMC',              unit: ''      },
                    { k: 'gordura_visceral',       label: 'Gordura Visceral', unit: ' nív.' },
                    { k: 'metabolismo_basal',      label: 'Metab. Basal',     unit: ' kcal' },
                  ].map(({ k, label, unit }) => {
                    const d = variacoes?.[k];
                    const isGood = goodDown(k) ? d < 0 : d > 0;
                    return (
                      <tr key={k} style={{ borderBottom: '0.5pt solid #ebe3da' }}>
                        <td style={{ padding: '3pt 6pt', fontWeight: 500 }}>{emo(label)}</td>
                        <td style={{ padding: '3pt 6pt', textAlign: 'center' }}>{fmtNum(primeira[k])}{unit}</td>
                        <td style={{ padding: '3pt 6pt', textAlign: 'center', fontWeight: 600 }}>{fmtNum(ultima[k])}{unit}</td>
                        <td style={{ padding: '3pt 6pt', textAlign: 'center', fontWeight: 600,
                          color: d !== undefined ? (isGood ? '#46714f' : d === 0 ? '#736b64' : '#a8483f') : '#736b64' }}>
                          {d !== undefined ? `${d > 0 ? '+' : ''}${d}${unit}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })() : (
          <div style={{ padding: '14pt', textAlign: 'center', background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
            border: `1pt solid ${BRAND}50`, borderRadius: '8pt' }}>
            <p style={{ margin: 0, fontSize: '10pt', color: BRAND2, fontWeight: 600, fontFamily: SERIF }}>
              <Ico e="🚀" sp />Este é o ponto de partida da sua jornada!
            </p>
            <p style={{ margin: '4pt 0 0', fontSize: '8.5pt', color: '#736b64', fontFamily: FONT }}>
              Na próxima consulta você já poderá ver sua evolução aqui. Vamos juntas!
            </p>
          </div>
        )}
      </div>

      {/* PLANO ALIMENTAR */}
      {diet_plan && (
        <div className="no-break" style={{ marginBottom: '14pt' }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '11pt', fontWeight: 600, color: '#2f2a26',
            margin: '0 0 5pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
            Seu Plano Alimentar — {diet_plan.plan_name}
          </h2>
          {/* Resumo calórico */}
          <div style={{ display: 'flex', gap: '8pt', marginBottom: '8pt' }}>
            {emo([
              { label: '🔥 Calorias', v: `${diet_plan.daily_calories} kcal/dia`, big: true },
              { label: '🥩 Proteínas', v: `${fmtNum(diet_plan.protein_grams)} g` },
              { label: '🌾 Carboidratos', v: `${fmtNum(diet_plan.carbs_grams)} g` },
              { label: '🥑 Gorduras', v: `${fmtNum(diet_plan.fat_grams)} g` },
              { label: '💧 Água', v: `${diet_plan.water_recommendation} L/dia` },
            ].map(({ label, v, big }) => (
              <div key={label} style={{ flex: 1, textAlign: 'center', padding: '6pt 4pt',
                background: big ? `${BRAND}20` : '#fbf8f5',
                border: `1pt solid ${big ? BRAND : '#f5ece2'}`, borderRadius: '6pt' }}>
                <p style={{ margin: '0 0 2pt', fontSize: '7pt', color: '#736b64', fontFamily: FONT }}>{emo(label)}</p>
                <p style={{ margin: 0, fontSize: big ? '11pt' : '9pt', fontWeight: 600,
                  color: big ? BRAND2 : '#2f2a26', fontFamily: FONT }}>{v}</p>
              </div>
            )))}
          </div>
          {/* Refeições em cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5pt' }}>
            {mealList(diet_plan.meals_plan).map((meal, i) => {
              const bg  = MEAL_COLORS[meal._key] || '#e8c9a8';
              return (
                <div key={i} className="no-break" style={{ padding: '6pt 8pt', borderRadius: '6pt',
                  borderLeft: `4pt solid ${bg}`, background: '#ffffff',
                  boxShadow: '0 1pt 3pt rgba(0,0,0,.07)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3pt' }}>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: '8pt', color: '#2f2a26', fontFamily: FONT }}>
                      {emo(meal.nome)}
                    </p>
                    <p style={{ margin: 0, fontSize: '7.5pt', color: '#736b64', fontFamily: FONT }}>
                      {meal.horario}
                    </p>
                  </div>
                  <p style={{ margin: '0 0 3pt', fontSize: '7pt', color: BRAND2, fontWeight: 600, fontFamily: FONT }}>
                    {meal.calorias || 0} kcal
                  </p>
                  <ul style={{ margin: 0, paddingLeft: '11pt', fontSize: '7.5pt', color: '#4a4440', lineHeight: 1.6 }}>
                    {(meal.alimentos || []).map((a, j) => (
                      <li key={j}>{a.alimento} — <span style={{ color: '#736b64' }}>{a.porcao}</span></li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SUPLEMENTAÇÃO */}
      {diet_plan?.supplements?.length > 0 && (
        <div className="no-break" style={{ marginBottom: '14pt' }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '11pt', fontWeight: 600, color: '#2f2a26',
            margin: '0 0 6pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
            <Ico e="💊" sp />Sua Suplementação
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '5pt' }}>
            {diet_plan.supplements.map((s, i) => (
              <div key={i} style={{ padding: '6pt 8pt', background: '#edf1f6',
                border: '1pt solid #b8c7d8', borderRadius: '6pt' }}>
                <p style={{ margin: '0 0 1pt', fontWeight: 600, fontSize: '8pt', color: '#4f6a86', fontFamily: FONT }}>
                  {emo(s.nome)}
                </p>
                <p style={{ margin: 0, fontSize: '7.5pt', color: '#736b64', fontFamily: FONT }}>
                  {[s.dosagem, s.momento_uso].filter(Boolean).join(' — ')}
                </p>
              </div>
            ))}
          </div>
          <p style={{ margin: '5pt 0 0', fontSize: '7.5pt', color: '#736b64', fontStyle: 'italic', textAlign: 'center', fontFamily: FONT }}>
            <Ico e="💊" sp />Lembre-se de tomar seus suplementos conforme orientado.
          </p>
        </div>
      )}

      {/* DICAS PERSONALIZADAS */}
      {diet_plan?.guidelines && (
        <div className="no-break" style={{ marginBottom: '14pt', padding: '10pt 14pt',
          background: 'linear-gradient(135deg,#edf1f6,#fbf8f5)',
          border: '1pt solid #edf1f6', borderRadius: '8pt' }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#2f2a26',
            margin: '0 0 6pt' }}>
            <Ico e="✨" sp />Dicas e Orientações Personalizadas
          </h2>
          <ul style={{ margin: 0, paddingLeft: '13pt', fontSize: '8.5pt', color: '#4a4440',
            lineHeight: 1.75, fontFamily: FONT }}>
            {diet_plan.guidelines.replace(/\\n/g, '\n').split('\n').filter(l => l.trim()).map((line, i) => (
              <li key={i}>{line.trim()}</li>
            ))}
          </ul>
        </div>
      )}

      {/* PRÓXIMO RETORNO */}
      {proximo_retorno && (
        <div className="no-break" style={{ marginBottom: '14pt', padding: '12pt 16pt',
          background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
          border: `2pt solid ${BRAND}`, borderRadius: '10pt', textAlign: 'center' }}>
          <p style={{ margin: '0 0 4pt', fontSize: '8pt', color: '#736b64', textTransform: 'uppercase',
            letterSpacing: '0.08em', fontFamily: FONT }}><Ico e="📅" sp />Próxima Consulta Sugerida</p>
          <p style={{ margin: '0 0 6pt', fontFamily: SERIF, fontSize: '14pt', fontWeight: 600, color: '#2f2a26' }}>
            {proximo_retorno}
          </p>
          <p style={{ margin: 0, fontSize: '8pt', color: '#736b64', fontFamily: FONT, lineHeight: 1.6 }}>
            Anote na agenda e não esqueça! <Ico e="📓" sp />Seu acompanhamento é fundamental
            para alcançar seus objetivos. <Ico e="💛" />
          </p>
        </div>
      )}

      {/* MENSAGEM MOTIVACIONAL + ASSINATURA */}
      <div className="no-break" style={{ marginBottom: '14pt', padding: '12pt 16pt',
        background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
        border: `1pt solid ${BRAND}50`, borderRadius: '10pt' }}>
        <p style={{ fontFamily: SERIF, fontSize: '10.5pt', fontStyle: 'italic', color: '#9a6a42',
          margin: '0 0 10pt', lineHeight: 1.7, textAlign: 'center' }}>
          "Cada escolha que você faz hoje é um investimento na versão mais saudável e feliz de você.
          Você está no caminho certo — continue! <Ico e="🌟" />"
        </p>
        <div style={{ borderTop: '0.8pt solid #e8c9a8', paddingTop: '8pt', textAlign: 'center' }}>
          <p style={{ margin: '0 0 2pt', fontWeight: 600, fontSize: '9pt', color: '#2f2a26', fontFamily: FONT }}>
            Com carinho, {nutri?.name_display || 'Sua Nutricionista'}
          </p>
          {nutri?.crn && (
            <p style={{ margin: '0 0 2pt', fontSize: '8pt', color: '#736b64', fontFamily: FONT }}>{nutri.crn}</p>
          )}
          <p style={{ margin: 0, fontSize: '8pt', color: '#736b64', fontFamily: FONT }}>
            {marca.businessName || ''}{nutri?.email ? `${marca.businessName ? ' · ' : ''}${nutri.email}` : ''}
            {nutri?.phone ? ` · ${nutri.phone}` : ''}
          </p>
        </div>
      </div>

      {/* RODAPÉ */}
      <div style={{ borderTop: '0.8pt solid #d9d0c6', paddingTop: '6pt',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <img src={LOGO_URL} alt={marca.businessName || 'LevNutri'} style={{ ...reportLogoStyle(marca, 18), objectFit: 'contain' }}/>
        <p style={{ margin: 0, fontSize: '6.5pt', color: '#d9d0c6', fontFamily: FONT }}>
          Este documento é pessoal e intransferível.{marca.businessName ? ` ${marca.businessName}.` : ''} Gerado com LevNutri.
        </p>
      </div>
    </div>
  );
}

// ─── Print Portals ────────────────────────────────────────────────────────────

function PrintProntuario({ data, notes }) {
  const content = (
    <div className="lev-relatorio-print">
      <ProntuarioContent data={data} notes={notes} />
    </div>
  );
  return ReactDOM.createPortal(content, document.body);
}

function PrintResumoPaciente({ data }) {
  const content = (
    <div className="lev-relatorio-print">
      <ResumoPacienteContent data={data} />
    </div>
  );
  return ReactDOM.createPortal(content, document.body);
}

// ─── Preview Modal ────────────────────────────────────────────────────────────

function PreviewModal({ type, data, notes, onClose, onPrint }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start',
      justifyContent: 'center', padding: '1rem', overflowY: 'auto',
    }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: '#ffffff', borderRadius: '12px', width: '100%', maxWidth: '794px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.35)', margin: 'auto',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Barra de ações */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.75rem 1.25rem', background: '#fbf8f5', borderRadius: '12px 12px 0 0',
          borderBottom: '1px solid #e8c9a8', gap: '0.75rem' }}>
          <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', color: '#2f2a26', fontFamily: FONT }}>
            {emo(type === 'nutricionista' ? '📋 Prontuário Interno' : '🌸 Resumo para a Paciente')} — Pré-visualização
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onPrint} style={{
              padding: '0.45rem 1rem', background: BRAND, color: '#ffffff',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.85rem', fontFamily: FONT,
            }}>
              <Ico e="🖨" sp />Imprimir / Salvar PDF
            </button>
            <button onClick={onClose} style={{
              padding: '0.45rem 0.9rem', background: '#f6f0ea', color: '#4a4440',
              border: 'none', borderRadius: '8px', cursor: 'pointer',
              fontWeight: 500, fontSize: '0.85rem', fontFamily: FONT,
            }}>
              <Ico e="✕" sp />Fechar
            </button>
          </div>
        </div>
        {type === 'paciente' && <ReportEmail patient={data.patient} />}
        {/* Conteúdo do relatório */}
        <div style={{ padding: '2rem', overflowY: 'auto', maxHeight: '80vh' }}>
          {type === 'nutricionista'
            ? <ProntuarioContent data={data} notes={notes} />
            : <ResumoPacienteContent data={data} />}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════

const S = {
  page:      { padding: '1.5rem 2rem', maxWidth: '900px', margin: '0 auto' },
  header:    { marginBottom: '1.75rem' },
  title:     { margin: '0 0 0.25rem', fontSize: '1.5rem', fontWeight: 600, color: '#2f2a26', fontFamily: FONT },
  subtitle:  { margin: 0, fontSize: '0.875rem', color: '#736b64', fontFamily: FONT },
  card:      { background: '#ffffff', borderRadius: '12px', border: '1px solid #f5ece2',
               boxShadow: '0 1px 4px rgba(0,0,0,.06)', padding: '1.25rem', marginBottom: '1rem' },
  input:     { width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid #e8c9a8',
               borderRadius: '8px', fontSize: '0.9rem', fontFamily: FONT, outline: 'none',
               background: '#ffffff', boxSizing: 'border-box', color: '#2f2a26' },
  label:     { display: 'block', marginBottom: '0.4rem', fontSize: '0.8rem', fontWeight: 600,
               color: '#736b64', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: FONT },
  reportCard: {
    background: '#ffffff', borderRadius: '14px', border: '1.5px solid #f5ece2',
    boxShadow: '0 2px 8px rgba(0,0,0,.06)', padding: '1.5rem',
    display: 'flex', flexDirection: 'column', gap: '0.75rem',
  },
  btnPrimary: {
    padding: '0.6rem 1.25rem', background: BRAND, color: '#ffffff', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
    fontFamily: FONT, flex: 1,
  },
  btnSecondary: {
    padding: '0.6rem 1.25rem', background: '#ebe3da', color: '#9a6a42', border: 'none',
    borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
    fontFamily: FONT, flex: 1,
  },
  emptyMsg:  { textAlign: 'center', color: '#d9d0c6', padding: '2rem 0', fontFamily: FONT, margin: 0 },
  tag:       { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '9999px',
               fontSize: '0.75rem', fontWeight: 600, fontFamily: FONT },
};

export default function Relatorios({ patientId: initialPatientId, onNavigate }) {
  const isMobile = useIsMobile();
  const [patients, setPatients]         = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedId, setSelectedId]     = useState(initialPatientId || '');
  const [reportData, setReportData]     = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [notes, setNotes]               = useState('');
  const [preview, setPreview]           = useState(null);   // 'nutricionista' | 'paciente' | null
  const [printMode, setPrintMode]       = useState(null);   // same values
  const [showDocumento, setShowDocumento] = useState(false);

  useEffect(() => {
    patientsService.getAll().then(res => { if (res.success) setPatients(res.data); });
  }, []);

  useEffect(() => {
    if (initialPatientId && patients.length > 0) {
      const p = patients.find(p => String(p.id) === String(initialPatientId));
      if (p) { setPatientSearch(p.name); setSelectedId(initialPatientId); }
    }
  }, [initialPatientId, patients]);

  useEffect(() => {
    if (!selectedId) { setReportData(null); return; }
    setLoading(true); setError('');
    reportsService.getData(selectedId)
      .then(res => {
        if (res.success) setReportData(res.data);
        else setError(res.error || 'Erro ao carregar dados');
      })
      .catch(() => setError('Erro de comunicação com o servidor'))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setPatientSearch(val);
    // se o usuário apagou, limpar seleção
    if (!val) { setSelectedId(''); setReportData(null); }
  }, []);

  const handlePatientSelect = useCallback((patient) => {
    setPatientSearch(patient.name);
    setSelectedId(String(patient.id));
    setNotes('');
    setPreview(null);
    setPrintMode(null);
  }, []);

  const handlePrint = useCallback((type) => {
    setPrintMode(type);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(null), 500);
    }, 200);
  }, []);

  const filteredPatients = patients.filter(p =>
    p.name.toLowerCase().includes(patientSearch.toLowerCase()) && patientSearch.length > 0
  ).slice(0, 8);

  const showDropdown = patientSearch.length > 0 && !selectedId && filteredPatients.length > 0;

  const pd = reportData;

  return (
    <div style={S.page}>
      <RelatorioCss />

      {/* Print portals */}
      {printMode === 'nutricionista' && pd && <PrintProntuario data={pd} notes={notes} />}
      {printMode === 'paciente'      && pd && <PrintResumoPaciente data={pd} />}

      {/* Preview modal */}
      {preview && pd && (
        <PreviewModal
          type={preview}
          data={pd}
          notes={notes}
          onClose={() => setPreview(null)}
          onPrint={() => { setPreview(null); handlePrint(preview); }}
        />
      )}

      {/* Documento do paciente */}
      {showDocumento && pd && (
        <DocumentoPacienteModal
          patientId={selectedId}
          patientName={pd.patient.name}
          onClose={() => setShowDocumento(false)}
        />
      )}

      {/* Cabeçalho */}
      <div style={S.header}>
        <h1 style={S.title}><Ico e="📊" sp />Relatórios</h1>
        <p style={S.subtitle}>
          Gere e imprima o prontuário interno e o resumo para a paciente
        </p>
      </div>

      {/* Busca de paciente */}
      <div style={{ ...S.card, position: 'relative', zIndex: 10 }}>
        <label style={S.label}>Buscar Paciente</label>
        <input
          style={S.input}
          type="text"
          placeholder="Digite o nome da paciente..."
          value={patientSearch}
          onChange={handleSearchChange}
          onFocus={() => { if (selectedId) setSelectedId(''); }}
          autoComplete="off"
        />
        {/* Dropdown */}
        {showDropdown && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0,
            background: '#ffffff', border: '1.5px solid #e8c9a8', borderTop: 'none',
            borderRadius: '0 0 10px 10px', boxShadow: '0 8px 24px rgba(0,0,0,.12)',
            zIndex: 100, overflow: 'hidden' }}>
            {filteredPatients.map(p => (
              <button key={p.id} onClick={() => handlePatientSelect(p)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem',
                  width: '100%', padding: '0.75rem 1rem', border: 'none',
                  background: 'none', cursor: 'pointer', textAlign: 'left',
                  borderBottom: '1px solid #fbf8f5', fontFamily: FONT }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%',
                  background: `${BRAND}30`, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontWeight: 600, color: BRAND2, flexShrink: 0 }}>
                  {p.name.charAt(0)}
                </div>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem', color: '#2f2a26' }}>{p.name}</p>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#736b64' }}>
                    {emo(OBJECTIVES[p.objective]?.label || p.objective || '')}
                    {p.status && p.status !== 'ativo' ? ` · ${p.status}` : ''}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={S.card}>
          <p style={S.emptyMsg}>Carregando dados da paciente...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ ...S.card, borderColor: '#c46a60', background: '#f8ebe9' }}>
          <p style={{ margin: 0, color: '#a8483f', fontSize: '0.875rem', fontFamily: FONT }}><Ico e="⚠" /> {error}</p>
        </div>
      )}

      {/* State inicial */}
      {!selectedId && !loading && !error && (
        <div style={S.card}>
          <p style={S.emptyMsg}>Selecione uma paciente para gerar os relatórios.</p>
        </div>
      )}

      {/* Relatórios disponíveis */}
      {pd && !loading && (
        <>
          {/* Resumo da paciente selecionada */}
          <div style={{ ...S.card, display: 'flex', alignItems: 'center', gap: '1.25rem',
            background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)', borderColor: `${BRAND}50` }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%',
              background: `${BRAND}30`, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '1.4rem', color: BRAND2, flexShrink: 0 }}>
              {pd.patient.name.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 2px', fontWeight: 600, fontSize: '1.05rem', color: '#2f2a26', fontFamily: FONT }}>
                {pd.patient.name}
              </p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#736b64', fontFamily: FONT }}>
                {emo(OBJECTIVES[pd.patient.objective]?.icon)} {emo(OBJECTIVES[pd.patient.objective]?.label || '')}
                {pd.total_consultas > 0 ? ` · ${pd.total_consultas} consulta${pd.total_consultas !== 1 ? 's' : ''}` : ' · Sem consultas'}
                {pd.evolucao?.ultima ? ` · Última: ${fmtDate(pd.evolucao.ultima.data_medicao)}` : ''}
              </p>
            </div>
            {pd.diet_plan && (
              <span style={{ ...S.tag, background: `${BRAND}20`, color: BRAND2 }}>
                <Ico e="✅" sp />Plano ativo
              </span>
            )}
            {pd.proximo_retorno && (
              <span style={{ ...S.tag, background: '#f6f0ea', color: '#46714f' }}>
                <Ico e="📅" /> {pd.proximo_retorno}
              </span>
            )}
          </div>

          {/* Documento unificado */}
          <div style={{ ...S.reportCard, background: 'linear-gradient(160deg,#fbf8f5,#fbf8f5)',
            borderColor: BRAND, flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'stretch' : 'center', gap: isMobile ? '0.75rem' : '1.5rem',
            marginBottom: '0', padding: '1.25rem 1.5rem' }}>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 4px', fontSize: '1.5rem' }}><Ico e="📄" /></p>
              <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 600,
                color: '#2f2a26', fontFamily: FONT }}>
                Documento Unificado para a Paciente
              </h3>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#736b64', lineHeight: 1.5, fontFamily: FONT }}>
                PDF profissional com Plano Alimentar (Página 1) e Recomendações Nutricionais (Página 2).
                Ideal para entregar após a consulta.
              </p>
            </div>
            <button
              style={{ ...S.btnPrimary, flex: '0 0 auto', background: BRAND2,
                padding: '0.65rem 1.5rem', fontSize: '0.9rem' }}
              onClick={() => setShowDocumento(true)}
            >
              <Ico e="📄" sp />Gerar Documento
            </button>
          </div>

          {/* Cards dos 2 relatórios */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.25rem', marginTop: '1rem' }}>

            {/* CARD 1 — Prontuário Interno */}
            <div style={S.reportCard}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '1.3rem' }}><Ico e="📋" /></p>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 600, color: '#2f2a26', fontFamily: FONT }}>
                  Prontuário da Nutricionista
                </h3>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#736b64', lineHeight: 1.5, fontFamily: FONT }}>
                  Uso exclusivo interno. Lembrete rápido com anamnese, evolução da bioimpedância,
                  histórico de planos e espaço para anotações.
                </p>
              </div>
              <div style={{ padding: '0.6rem 0.75rem', background: '#fbf8f5',
                borderRadius: '8px', border: '1px solid #f5ece2' }}>
                <p style={{ ...S.label, marginBottom: '0.35rem', fontSize: '0.73rem' }}>
                  Anotações para esta consulta (não salvo)
                </p>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Ex: Paciente relatou dificuldade com o jantar, revisar suplementação de magnésio..."
                  style={{ width: '100%', minHeight: '70px', padding: '0.5rem',
                    border: '1px solid #e8c9a8', borderRadius: '6px', fontSize: '0.82rem',
                    fontFamily: FONT, resize: 'vertical', boxSizing: 'border-box',
                    color: '#4a4440', background: '#ffffff', outline: 'none' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button style={S.btnSecondary} onClick={() => setPreview('nutricionista')}>
                  <Ico e="👁" sp />Visualizar
                </button>
                <button style={S.btnPrimary} onClick={() => handlePrint('nutricionista')}>
                  <Ico e="🖨" sp />Imprimir
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '0.73rem', color: '#d9d0c6', textAlign: 'center', fontFamily: FONT }}>
                <Ico e="🔴" sp />Confidencial — Uso interno exclusivo
              </p>
            </div>

            {/* CARD 2 — Resumo para a Paciente */}
            <div style={{ ...S.reportCard, background: 'linear-gradient(160deg,#ffffff,#fbf8f5)' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '1.3rem' }}><Ico e="🌸" /></p>
                <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 600, color: '#2f2a26', fontFamily: FONT }}>
                  Resumo para a Paciente
                </h3>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#736b64', lineHeight: 1.5, fontFamily: FONT }}>
                  Documento elegante entregue após a consulta. Composição corporal,
                  evolução, plano alimentar, suplementação e próximo retorno.
                </p>
              </div>
              {/* Info rápida */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '0.4rem' }}>
                {emo([
                  ['⚖️', 'Peso', pd.evolucao?.ultima?.peso ? `${fmtNum(pd.evolucao.ultima.peso)} kg` : '-'],
                  ['🔥', 'Kcal/dia', pd.diet_plan ? `${pd.diet_plan.daily_calories}` : '-'],
                  ['📅', 'Retorno', pd.proximo_retorno ? pd.proximo_retorno.split(' ').slice(2).join(' ') : '-'],
                ].map(([icon, label, val]) => (
                  <div key={label} style={{ textAlign: 'center', padding: '0.5rem 0.25rem',
                    background: '#fbf8f5', borderRadius: '8px' }}>
                    <p style={{ margin: '0 0 1px', fontSize: '1.1rem' }}>{emo(icon)}</p>
                    <p style={{ margin: '0 0 1px', fontSize: '0.68rem', color: '#a39b93', fontFamily: FONT }}>{emo(label)}</p>
                    <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 600, color: '#2f2a26', fontFamily: FONT }}>{val}</p>
                  </div>
                )))}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button style={S.btnSecondary} onClick={() => setPreview('paciente')}>
                  <Ico e="👁" sp />Visualizar
                </button>
                <button style={{ ...S.btnPrimary, background: '#c46a60' }} onClick={() => handlePrint('paciente')}>
                  <Ico e="🖨" sp />Imprimir
                </button>
              </div>
              <p style={{ margin: 0, fontSize: '0.73rem', color: '#d9d0c6', textAlign: 'center', fontFamily: FONT }}>
                <Ico e="🌸" sp />Documento premium para a paciente levar para casa
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
